import type { FC } from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import H2 from '@/components/H2'
import LoadingSpinner from '@/components/LoadingSpinner'
import ctx from '@/context/AppContext'
import { useVerifyAdverseCreditMutation } from '@/lib/apollo'
import type { AdverseCredit } from '@/types/adverseCredit'
import { AdverseCreditType } from '@/types/adverseCredit'

import { toAdverseCreditPayload } from './utils'
import VerifyCreditFields from './VerifyCreditFields'
import type { Fields } from './VerifyCreditFields'

const ContentContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`

interface Props {
  customerId: string
  subjectId: string
  identityConfirmed: boolean
  residencyConfirmed: boolean
  adverseCredit: AdverseCredit[]
  onClose: () => void
}

const VerifyCreditSidebar: FC<Props> = ({
  customerId,
  subjectId,
  onClose,
  identityConfirmed,
  residencyConfirmed,
  adverseCredit,
}) => {
  const [fields, setFields] = useState<Fields>({
    identityConfirmed,
    residencyConfirmed,
    ccjs: adverseCredit.filter(({ type }) => type === AdverseCreditType.ccj),
    ivaBankruptcies: adverseCredit.filter(
      ({ type }) => type === AdverseCreditType.ivaBankruptcy,
    ),
    other: adverseCredit.filter(({ type }) => type === AdverseCreditType.other),
  })

  const [editAdverseCredit, { loading, error, data }] = useVerifyAdverseCreditMutation()

  const { personId } = useContext(ctx)

  const onConfirm = () =>
    editAdverseCredit({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        identityConfirmed: fields.identityConfirmed,
        residencyConfirmed: fields.residencyConfirmed,
        adverseCredit: [...fields.ccjs, ...fields.ivaBankruptcies, ...fields.other].map(
          toAdverseCreditPayload,
        ),
      },
    })

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<H2>Verify credit</H2>}
      content={
        <ContentContainer>
          {data !== undefined || loading ? (
            <LoadingSpinner />
          ) : (
            <VerifyCreditFields values={fields} onChangeValues={setFields} />
          )}
        </ContentContainer>
      }
      footer={
        <>
          {error && <div>An unexpected error occurred. {error?.message}</div>}
          <div className="side-by-side-flex-end">
            <AlternateButton onClick={onClose}>Cancel</AlternateButton>
            <Button disabled={data !== undefined || loading} onClick={onConfirm}>
              Confirm
            </Button>
          </div>
        </>
      }
    />
  )
}

export default VerifyCreditSidebar
